import React, { useState, useContext } from 'react';

import moment from 'moment';

/** context */
import { UserContext } from '../../context/UserContext';
import { formatUSDCurrency } from '../../helpers/format';
import { getAuthenticatedContractURL, updateApplication } from '../../service/api';

export default function PaymentSuccessful({ className, gotoNextStep }) {
  const { appData, profile, updateAppData, setLoading } = useContext(UserContext);
  const [showAll, setShowAll] = useState(false);
  const [flagConfirm, setFlagConfirm] = useState(false);
  const [showError, setShowError] = useState(false);

  const multiApprovedRateFeature = appData?.applicationFeatures?.find(
    (feature) => feature.itemType === 'MultiApprovedRateProperty'
  );

  const autopayFeature = appData?.applicationFeatures.find((feature) => feature.name === 'autopay');

  const autopay = autopayFeature.value?.length ? autopayFeature.value[0]?.consented : false;
  const approvedRateDetails = multiApprovedRateFeature?.value?.rates
    ? multiApprovedRateFeature?.value?.rates[multiApprovedRateFeature?.value?.selectedIndex]
    : {};

  const scheduledItems = approvedRateDetails?.amortizationScheduleList?.slice(1);

  const toggleShowAll = () => {
    setShowAll((val) => !val);
  };

  const handleCheckTermscon = (e) => {
    const termsconStatus = e.target.checked;
    setFlagConfirm(termsconStatus);
  };

  const getAuthenticatedContract = (anchor) => {
    return getAuthenticatedContractURL(appData.id, anchor);
  };
  const handleSubmit = async () => {
    setShowError(false);
    if (!flagConfirm) {
      setShowError(true);
      return;
    }
    setLoading(true);
    try {
      const contractFeature = appData?.applicationFeatures.find(
        (feature) => feature.name === 'contract'
      );

      const updatedData = await updateApplication([
        {
          itemType: contractFeature.itemType,
          dataType: contractFeature.dataType,
          name: contractFeature.name,
          value: [
            {
              userId: profile.id,
              consented: true,
            },
          ],
        },
      ]);

      if (updatedData.data) {
        updateAppData(updatedData.data);
      }

      setLoading(false);
    } catch (error) {
      console.log('error', error);
      const errors = error.response.data.errors;
      if (errors.length) {
        setError(errors[0].message);
      }
      setLoading(false);
      return;
    }

    gotoNextStep();
  };

  return (
    <div className={className}>
      <div className={`${className}__title`}>
        <h1>Confirm your financing terms</h1>
      </div>
      <div className={`${className}__divider`} />
      <div className={`${className}__order-items`}>
        <div className={`${className}__order-items__item`}>
          <div className={`${className}__order-items__item-key`}>
            <span className="bullet">&#8226;</span>
            Annual percentage rate
          </div>
          <div className={`${className}__order-items__item-value`}>
            {approvedRateDetails?.approvedRate?.approvedApr}%
          </div>
        </div>
        <div className={`${className}__order-items__item`}>
          <div className={`${className}__order-items__item-key`}>
            <span className="bullet">&#8226;</span>
            Total finance charge
          </div>
          <div className={`${className}__order-items__item-value`}>
            {formatUSDCurrency(approvedRateDetails?.totalInterest)}
          </div>
        </div>
        <div className={`${className}__order-items__item`}>
          <div className={`${className}__order-items__item-key`}>
            <span className="bullet">&#8226;</span>
            Total amount financed
          </div>
          <div className={`${className}__order-items__item-value`}>
            {formatUSDCurrency(approvedRateDetails?.approvedRate?.approvedAmount)}
          </div>
        </div>
        <div className={`${className}__order-items__item`}>
          <div className={`${className}__order-items__item-key`}>
            <span className="bullet">&#8226;</span>
            Total of payments
          </div>
          <div className={`${className}__order-items__item-value`}>
            {formatUSDCurrency(approvedRateDetails?.totalPayment)}
          </div>
        </div>
      </div>
      <div className={`${className}__divider`} />
      <div className={`${className}__payment-description`}>
        <p>
          Your payment will be due monthly starting on{' '}
          <span>{moment(scheduledItems[0]?.paymentDate).format('MMMM D, YYYY')}</span>
        </p>
      </div>
      <div className={`${className}__divider`} />
      <div className={`${className}__payment-terms`}>
        <h1>Payment Schedule</h1>
      </div>
      <div className={`${className}__divider`} />
      <div className={`${className}__payment-description`}>
        You are currently <span className="upper">{!autopay && 'not'}</span> <span>enrolled</span>{' '}
        in Auto Pay.{' '}
        {autopay
          ? 'You can change your selection at any time by logging into the self servicing portal.'
          : 'You can enroll in Auto Pay by logging into the self servicing portal.'}
      </div>
      <div className={`${className}__divider`} />
      <div className={`${className}__payment-schedule`}>
        <div className={`${className}__schedule-item`}>
          <div className={`${className}__schedule-item__label`}>Payment Schedule</div>
        </div>
        {scheduledItems?.slice(0, showAll ? scheduledItems.length : 3).map((item, index) => (
          <div className={`${className}__schedule-item`} key={`schedule-${index}`}>
            <div className={`${className}__schedule-item__data`}>
              <div className={`${className}__schedule-item__data__date`}>
                {moment(item.paymentDate).format('MM-DD-YYYY')}
              </div>
              <div className={`${className}__schedule-item__data__amount`}>
                {formatUSDCurrency(item.paymentAmount)}
              </div>
            </div>
            <div className={`${className}__divider`} />
          </div>
        ))}
        <div className={`${className}__schedule-item`}>
          <div className={`${className}__schedule-item__label more-link`} onClick={toggleShowAll}>
            View {!showAll ? 'more' : 'less'}
          </div>
        </div>
        <div className={`${className}__payment-termscon`}>
          <input type="checkbox" id="termscon" onChange={(e) => handleCheckTermscon(e)} />
          <label htmlFor="termscon">
            By clicking CONFIRM FINANCING, I acknowledge that I have reviewed and agree to the terms
            and conditions of the{' '}
            <a
              className="green-link"
              href={getAuthenticatedContract('contract')}
              target="_blank"
              rel="noreferrer"
            >
              Retail Installment Contract
            </a>
            <span className="green-link"> WHICH INCLUDES A MANDATORY ARBITRATION PROVISION</span>,{' '}
            <a
              className="black-link"
              href={getAuthenticatedContract('medicalone')}
              target="_blank"
              rel="noreferrer"
            >
              E-Sign Consent
            </a>
            ,{' '}
            <a
              className="black-link"
              href={getAuthenticatedContract('privacy')}
              target="_blank"
              rel="noreferrer"
            >
              GLBA Privacy Notice
            </a>
            ,{' '}
            <a
              className="black-link"
              href={getAuthenticatedContract('medicaltwo')}
              target="_blank"
              rel="noreferrer"
            >
              Confirmation of Authorization for Release of Medical Information
            </a>{' '}
            and have received the{' '}
            <a
              className="black-link"
              href={getAuthenticatedContract('creditscore')}
              target="_blank"
              rel="noreferrer"
            >
              Risk-Based Pricing Notice
            </a>
            .
          </label>
        </div>
        {showError && (
          <div className={`${className}__payment-error`}>
            You must review and agree to the terms and conditions listed above to proceed.
          </div>
        )}
        <div className={`${className}__button-container`}>
          <button onClick={handleSubmit}>CONFIRM FINANCING</button>
        </div>
      </div>
    </div>
  );
}
